import { cn } from '@/lib/utils';
import type {
  CriteriaSection,
  FacesheetAnchorRef,
  FacesheetCriteriaRow,
} from '../types';
import { FacesheetCriteriaReferencesButton } from './FacesheetCriteriaReferencesButton';

interface FlattenedCriteriaRow {
  row: FacesheetCriteriaRow;
  depth: number;
}

const flattenCriteriaRows = (
  rows: FacesheetCriteriaRow[],
  depth = 0,
): FlattenedCriteriaRow[] =>
  rows.flatMap((row) => [
    { row, depth },
    ...flattenCriteriaRows(row.children ?? [], depth + 1),
  ]);

interface FacesheetCriteriaTableProps {
  rows: FacesheetCriteriaRow[];
  section: CriteriaSection;
  getAnchorRef: FacesheetAnchorRef;
  onOpenReferences: (
    row: FacesheetCriteriaRow,
    section: CriteriaSection,
    referenceId?: string,
  ) => void;
}

export const FacesheetCriteriaTable = ({
  rows,
  section,
  getAnchorRef,
  onOpenReferences,
}: FacesheetCriteriaTableProps) => {
  const flattenedRows = flattenCriteriaRows(rows);

  return (
    <div className="overflow-hidden rounded-md border border-gray-200 bg-white shadow-sm">
      <table className="w-full table-fixed border-collapse text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            <th
              scope="col"
              className="px-4 py-2 text-left font-medium text-gray-500"
            >
              Question
            </th>
            <th
              scope="col"
              className="w-20 px-4 py-2 text-left font-medium text-gray-500"
            >
              Answer
            </th>
            <th
              scope="col"
              className="px-4 py-2 text-left font-medium text-gray-500"
            >
              Reason
            </th>
            <th
              scope="col"
              className="w-32 px-4 py-2 text-left font-medium text-gray-500"
            >
              References
            </th>
          </tr>
        </thead>
        <tbody>
          {flattenedRows.map(({ row, depth }) => {
            const hasChildren = (row.children?.length ?? 0) > 0;
            const isHighlighted = Boolean(row.isHighlighted);

            return (
              <tr
                key={row.id}
                id={row.anchorId}
                ref={getAnchorRef(row.anchorId)}
                tabIndex={-1}
                className={cn(
                  'border-b border-gray-200 last:border-b-0',
                  isHighlighted && 'bg-yellow-200',
                )}
              >
                <td
                  className={cn(
                    'px-4 py-3 align-top text-gray-900',
                    isHighlighted && 'font-semibold',
                    depth === 0 &&
                      hasChildren &&
                      cn(
                        'border-b border-zinc-200 shadow-[inset_3px_0_0_0_#64748B]',
                        !isHighlighted && 'bg-white',
                      ),
                    depth > 0 && 'pl-8',
                  )}
                >
                  <div className="flex gap-2">
                    {depth > 0 ? (
                      <span
                        aria-hidden="true"
                        className="mt-1.5 h-2.5 w-2.5 shrink-0 border-b border-l border-slate-400"
                      />
                    ) : null}
                    <span>{row.question}</span>
                  </div>
                </td>
                <td className="px-4 py-3 align-top font-medium text-gray-900">
                  {row.answer}
                </td>
                <td
                  className={cn(
                    'px-4 py-3 align-top text-gray-900',
                    isHighlighted && 'font-semibold',
                  )}
                >
                  {row.reason}
                </td>
                <td className="px-4 py-3 align-top">
                  <FacesheetCriteriaReferencesButton
                    references={row.references}
                    onSelectReference={(referenceId) =>
                      onOpenReferences(row, section, referenceId)
                    }
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
